import { NavLink, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AlertsBell } from "@/components/AlertsBell";
import { SignOutIcon as SignOut, HouseIcon as House, FileTextIcon as FileText, ReceiptIcon as Receipt, ClockIcon as Clock, WrenchIcon as Wrench, GaugeIcon as Gauge, FolderSimpleIcon as FolderSimple, TicketIcon as TicketIcon, ShieldCheckIcon as ShieldCheck, DesktopIcon as Desktop, ToolboxIcon as Toolbox } from "@phosphor-icons/react";
import { useLang, translate, setLang } from "@/lib/i18n";

const NAV = [
  { to: "/portale", label: "Riepilogo", testid: "pnav-home", end: true, Icon: House },
  { to: "/portale/contratti", label: "Contratti", testid: "pnav-contratti", Icon: FileText },
  { to: "/portale/preventivi", label: "Preventivi", testid: "pnav-preventivi", Icon: Receipt },
  { to: "/portale/monte-ore", label: "Monte ore", testid: "pnav-monteore", Icon: Clock },
  { to: "/portale/lavorazioni", label: "Lavorazioni", testid: "pnav-lavorazioni", Icon: Wrench },
  { to: "/portale/riparazioni", label: "Riparazioni", testid: "pnav-riparazioni", Icon: Toolbox },
  { to: "/portale/dispositivi", label: "Fotocopiatrici", testid: "pnav-dispositivi", Icon: Gauge },
  { to: "/portale/dispositivi-gestiti", label: "Dispositivi", testid: "pnav-dispositivi-gestiti", Icon: Desktop },
  { to: "/portale/documentazione", label: "Documentazione", testid: "pnav-documentazione", Icon: FolderSimple },
  { to: "/portale/ticket", label: "Ticket", testid: "pnav-ticket", Icon: TicketIcon },
];

const ISO_NAV = { to: "/portale/iso27001", label: "Sicurezza ISO 27001", testid: "pnav-iso27001", Icon: ShieldCheck };

export function PortalLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const lang = useLang();
  const [isoVisible, setIsoVisible] = useState(false);
  useEffect(() => { api.get("/portal/iso27001").then(({ data }) => setIsoVisible(!!data.visible)).catch(() => {}); }, []);
  const navItems = isoVisible ? [...NAV, ISO_NAV] : NAV;

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const impersonating = typeof window !== "undefined" && !!localStorage.getItem("impersonator_token");
  const exitImpersonation = () => {
    const admin = localStorage.getItem("impersonator_token");
    if (admin) localStorage.setItem("token", admin);
    localStorage.removeItem("impersonator_token");
    window.location.href = "/clienti";
  };

  return (
    <div className="min-h-screen bg-secondary/30 flex">
      {impersonating && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-amber-500 text-white text-sm px-4 py-2 flex items-center justify-center gap-3" data-testid="impersonation-banner">
          <span>Stai visualizzando l'area cliente in modalità impersonazione.</span>
          <button onClick={exitImpersonation} className="underline font-semibold" data-testid="exit-impersonation-btn">Torna al pannello admin</button>
        </div>
      )}
      {/* Sidebar */}
      <aside className="w-64 shrink-0 bg-white border-r border-border flex-col hidden md:flex sticky top-0 h-screen" data-testid="portal-sidebar">
        <div className="h-16 flex items-center px-6 border-b border-border">
          <img src="/brand/logo.png" alt="Tecnologie" className="h-7 w-auto object-contain" />
        </div>
        <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
          {translate("Area cliente", lang)}
        </div>
        <nav className="flex-1 px-3 py-2 space-y-1 overflow-y-auto">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              className={({ isActive }) =>
                `group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                  isActive
                    ? "bg-primary/10 text-primary font-semibold shadow-sm ring-1 ring-primary/15"
                    : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <n.Icon size={18} weight={isActive ? "fill" : "regular"} className="shrink-0" />
                  <span className="truncate">{translate(n.label, lang)}</span>
                </>
              )}
            </NavLink>
          ))}
        </nav>
        {/* Language switch */}
        <div className="px-4 py-4 border-t border-border flex items-center justify-between">
          <span className="text-xs text-muted-foreground">{translate("Lingua", lang)}</span>
          <div className="flex items-center gap-1 text-xs border border-border rounded-md overflow-hidden" data-testid="portal-lang-switch">
            <button onClick={() => setLang("it")} data-testid="portal-lang-it"
              className={`px-2.5 py-1 transition-colors ${lang === "it" ? "bg-primary text-white font-semibold" : "text-muted-foreground hover:bg-secondary"}`}>IT</button>
            <button onClick={() => setLang("en")} data-testid="portal-lang-en"
              className={`px-2.5 py-1 transition-colors ${lang === "en" ? "bg-primary text-white font-semibold" : "text-muted-foreground hover:bg-secondary"}`}>EN</button>
          </div>
        </div>
      </aside>

      {/* Main column */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-white/80 backdrop-blur border-b border-border sticky top-0 z-20 flex items-center justify-end gap-4 px-6">
          {/* mobile nav (compact) */}
          <nav className="md:hidden flex items-center gap-1 mr-auto overflow-x-auto">
            {navItems.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} data-testid={`m-${n.testid}`}
                className={({ isActive }) => `px-2 py-1 rounded text-xs ${isActive ? "bg-primary/10 text-primary font-semibold" : "text-muted-foreground"}`}>
                <n.Icon size={18} />
              </NavLink>
            ))}
          </nav>
          <AlertsBell />
          <span className="text-sm text-muted-foreground hidden sm:inline">{user?.name}</span>
          <button
            data-testid="portal-logout-btn"
            onClick={handleLogout}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-destructive transition-colors"
          >
            <SignOut size={16} weight="bold" /> <span className="hidden sm:inline">{translate("Esci", lang)}</span>
          </button>
        </header>
        <main className="flex-1 max-w-6xl w-full mx-auto px-6 py-10">{children}</main>
        <div className="text-center text-xs text-muted-foreground py-6 border-t border-border leading-relaxed px-4">
          Tecnologie S.r.l. &mdash; Viale dell'Industria, 11 &mdash; 37023 Grezzana (VR)
          <br className="hidden sm:block" />
          <span className="sm:ml-1">assistenza@tecnologieverona.it &mdash; +39 045-4770241</span>
        </div>
      </div>
    </div>
  );
}
